/**
 * Country data used by PhoneInput and CountrySelect.
 * `iso2` = ISO 3166-1 alpha-2 (stored value everywhere in DB).
 * `dial_code` = international dial code (leading + not included).
 * Flag emoji is derived from ISO2 → Regional Indicator Symbols; renders as a
 * flag on modern platforms and falls back to a letter pair otherwise.
 *
 * Countries surfaced as "popular" appear at the top of both dropdowns. The
 * rest are sorted alphabetically by locale-specific name at render time.
 */

const raw = [
  ["DE", "Deutschland", "Germany", "49"],
  ["AT", "Österreich", "Austria", "43"],
  ["CH", "Schweiz", "Switzerland", "41"],
  ["FR", "Frankreich", "France", "33"],
  ["IT", "Italien", "Italy", "39"],
  ["ES", "Spanien", "Spain", "34"],
  ["NL", "Niederlande", "Netherlands", "31"],
  ["BE", "Belgien", "Belgium", "32"],
  ["GB", "Vereinigtes Königreich", "United Kingdom", "44"],
  ["US", "Vereinigte Staaten", "United States", "1"],
  // — rest, alphabetical by ISO
  ["AE", "Vereinigte Arabische Emirate", "United Arab Emirates", "971"],
  ["AR", "Argentinien", "Argentina", "54"],
  ["AU", "Australien", "Australia", "61"],
  ["BG", "Bulgarien", "Bulgaria", "359"],
  ["BR", "Brasilien", "Brazil", "55"],
  ["CA", "Kanada", "Canada", "1"],
  ["CL", "Chile", "Chile", "56"],
  ["CN", "China", "China", "86"],
  ["CO", "Kolumbien", "Colombia", "57"],
  ["CY", "Zypern", "Cyprus", "357"],
  ["CZ", "Tschechien", "Czechia", "420"],
  ["DK", "Dänemark", "Denmark", "45"],
  ["EE", "Estland", "Estonia", "372"],
  ["EG", "Ägypten", "Egypt", "20"],
  ["FI", "Finnland", "Finland", "358"],
  ["GR", "Griechenland", "Greece", "30"],
  ["HK", "Hongkong", "Hong Kong", "852"],
  ["HR", "Kroatien", "Croatia", "385"],
  ["HU", "Ungarn", "Hungary", "36"],
  ["IE", "Irland", "Ireland", "353"],
  ["IL", "Israel", "Israel", "972"],
  ["IN", "Indien", "India", "91"],
  ["IS", "Island", "Iceland", "354"],
  ["JP", "Japan", "Japan", "81"],
  ["KR", "Südkorea", "South Korea", "82"],
  ["LI", "Liechtenstein", "Liechtenstein", "423"],
  ["LT", "Litauen", "Lithuania", "370"],
  ["LU", "Luxemburg", "Luxembourg", "352"],
  ["LV", "Lettland", "Latvia", "371"],
  ["MA", "Marokko", "Morocco", "212"],
  ["MC", "Monaco", "Monaco", "377"],
  ["MT", "Malta", "Malta", "356"],
  ["MX", "Mexiko", "Mexico", "52"],
  ["MY", "Malaysia", "Malaysia", "60"],
  ["NO", "Norwegen", "Norway", "47"],
  ["NZ", "Neuseeland", "New Zealand", "64"],
  ["PE", "Peru", "Peru", "51"],
  ["PH", "Philippinen", "Philippines", "63"],
  ["PL", "Polen", "Poland", "48"],
  ["PT", "Portugal", "Portugal", "351"],
  ["QA", "Katar", "Qatar", "974"],
  ["RO", "Rumänien", "Romania", "40"],
  ["SA", "Saudi-Arabien", "Saudi Arabia", "966"],
  ["SE", "Schweden", "Sweden", "46"],
  ["SG", "Singapur", "Singapore", "65"],
  ["SI", "Slowenien", "Slovenia", "386"],
  ["SK", "Slowakei", "Slovakia", "421"],
  ["TH", "Thailand", "Thailand", "66"],
  ["TR", "Türkei", "Türkiye", "90"],
  ["UA", "Ukraine", "Ukraine", "380"],
  ["VN", "Vietnam", "Vietnam", "84"],
  ["ZA", "Südafrika", "South Africa", "27"],
];

const POPULAR_ISO = new Set([
  "DE", "AT", "CH", "FR", "IT", "ES", "NL", "BE", "GB", "US",
]);

/** Convert ISO2 to the emoji flag via regional-indicator symbols. */
function isoToEmoji(iso2) {
  if (!iso2 || iso2.length !== 2) return "";
  const base = 0x1F1E6 - "A".charCodeAt(0);
  return String.fromCodePoint(...iso2.toUpperCase().split("").map((c) => c.charCodeAt(0) + base));
}

export const COUNTRIES = raw.map(([iso2, name_de, name_en, dial_code]) => ({
  iso2,
  name_de,
  name_en,
  dial_code,
  flag: isoToEmoji(iso2),
  popular: POPULAR_ISO.has(iso2),
}));

const BY_ISO = new Map(COUNTRIES.map((c) => [c.iso2, c]));

/** Get a country by ISO2 (case-insensitive). Returns Germany as fallback. */
export function findCountry(iso2) {
  if (!iso2) return BY_ISO.get("DE");
  return BY_ISO.get(iso2.toUpperCase()) || BY_ISO.get("DE");
}

/** Localized name lookup — `lng` = "de" | "en". */
export function countryName(iso2, lng) {
  const c = findCountry(iso2);
  if (!c) return iso2 || "";
  return lng === "en" ? c.name_en : c.name_de;
}

/**
 * Returns countries sorted for display: popular first (fixed order), then
 * alphabetical by locale-specific name. Suitable for combobox lists.
 */
export function sortedCountries(lng) {
  const popularOrder = ["DE", "AT", "CH", "FR", "IT", "ES", "NL", "BE", "GB", "US"];
  const key = lng === "en" ? "name_en" : "name_de";
  const popular = popularOrder.map((iso) => BY_ISO.get(iso)).filter(Boolean);
  const rest = COUNTRIES.filter((c) => !c.popular).sort((a, b) =>
    a[key].localeCompare(b[key], lng)
  );
  return [...popular, ...rest];
}

/**
 * Parse an E.164-ish stored phone value ("+491701234567") into
 * { iso2, national }.  Longest-dial-code-match wins.  Falls back to DE for
 * empty / unknown inputs.
 */
export function parsePhone(value) {
  const raw = String(value || "").trim();
  if (!raw.startsWith("+")) {
    return { iso2: "DE", national: raw.replace(/\D/g, "") };
  }
  const digits = raw.slice(1).replace(/\D/g, "");
  // Longest dial-code prefix wins so +1... doesn't shadow +1876 etc.
  const dialCodes = [...new Set(COUNTRIES.map((c) => c.dial_code))].sort(
    (a, b) => b.length - a.length
  );
  for (const dc of dialCodes) {
    if (digits.startsWith(dc)) {
      // Prefer the "popular" mapping for shared dial codes (e.g. +1 → US, not CA).
      const preferred = COUNTRIES.find((c) => c.dial_code === dc && c.popular);
      const iso2 = (preferred || COUNTRIES.find((c) => c.dial_code === dc)).iso2;
      return { iso2, national: digits.slice(dc.length) };
    }
  }
  return { iso2: "DE", national: digits };
}

/** Combine dial code + national into E.164 storage form. */
export function joinPhone(iso2, national) {
  const c = findCountry(iso2);
  const digits = String(national || "").replace(/\D/g, "");
  if (!digits) return "";
  return `+${c.dial_code}${digits}`;
}
